import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, InputSuelto, Select, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  TIPOS, useCrearEncuesta, useEditarEncuesta, useEncuesta,
  type CuerpoDeEncuesta, type EncuestaDeLaLista, type OpcionDePregunta, type TipoDePregunta,
} from './api';

/**
 * FormularioDeEncuesta — el alta y la edición, con su editor de preguntas.
 *
 * ## Lo que cambia
 *
 * - **Editar una encuesta con respuestas ya no falla.** El servidor rechazaba
 *   la edición entera con un 400 mientras esta pantalla prometía «solo puedes
 *   editar el título y la descripción» y leía un `questions_locked` que nunca
 *   llegaba. Ahora es verdad: el título y la descripción se corrigen siempre,
 *   y lo que cambiaría el significado de lo respondido —las preguntas y si es
 *   anónima— se enseña, en gris y sin poder tocarlo.
 * - **El bloqueo era `pointer-events:none`**, que no impide llegar con el
 *   teclado ni escribir dentro. Ahora los campos bloqueados no se pintan.
 * - **Una pregunta de opciones necesita al menos dos con texto.** Se podía
 *   guardar una con cero, y salía en el formulario como un enunciado suelto que
 *   nadie podía responder. Lo comprueban las dos capas.
 */

/** Una pregunta mientras se escribe: aún no tiene id. */
interface Borrador {
  question_text: string;
  question_type: TipoDePregunta;
  options: OpcionDePregunta[];
  is_required: boolean;
}

const OPCIONES_NUEVAS = (): OpcionDePregunta[] =>
  [{ key: 'a', text: '' }, { key: 'b', text: '' }, { key: 'c', text: '' }];

const PREGUNTA_NUEVA = (): Borrador => ({
  question_text: '',
  question_type: 'single_choice',
  options: OPCIONES_NUEVAS(),
  is_required: true,
});

export function FormularioDeEncuesta({
  encuesta, onCerrar,
}: {
  /** `null` = una encuesta nueva. */
  encuesta: EncuestaDeLaLista | null;
  onCerrar: () => void;
}) {
  const editando = encuesta != null;
  const bloqueada = (encuesta?.response_count ?? 0) > 0;
  const cargada = useEncuesta(editando ? encuesta.id : null);

  const [titulo, setTitulo] = useState(encuesta?.title ?? '');
  const [descripcion, setDescripcion] = useState(encuesta?.description ?? '');
  const [anonima, setAnonima] = useState(encuesta?.is_anonymous ?? true);
  const [alFeed, setAlFeed] = useState(false);
  const [porCorreo, setPorCorreo] = useState(false);
  const [preguntas, setPreguntas] = useState<Borrador[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const crear = useCrearEncuesta();
  const editar = useEditarEncuesta();
  const guardando = crear.isPending || editar.isPending;

  // Las preguntas de la encuesta que se edita, cuando llegan.
  useEffect(() => {
    const d = cargada.data;
    if (!d) return;
    setTitulo(d.title);
    setDescripcion(d.description ?? '');
    setAnonima(d.is_anonymous);
    setPreguntas([...d.questions]
      .sort((a, b) => a.order - b.order)
      .map((q) => ({
        question_text: q.question_text,
        question_type: q.question_type,
        options: (q.options ?? []).map((o) => ({ key: o.key, text: o.text })),
        is_required: q.is_required,
      })));
  }, [cargada.data]);

  function cambiarPregunta(i: number, cambio: Partial<Borrador>) {
    setPreguntas((ps) => ps.map((p, j) => (j === i ? { ...p, ...cambio } : p)));
  }

  function cambiarTipo(i: number, tipo: TipoDePregunta) {
    const actual = preguntas[i];
    if (!actual) return;
    cambiarPregunta(i, {
      question_type: tipo,
      // Al pasar a un tipo de opciones sin ninguna escrita, se ofrecen tres,
      // como en el alta. Si ya las había, se conservan: volver de «escala» a
      // «única» no debería borrar lo que alguien escribió.
      options: TIPOS[tipo].conOpciones && !actual.options.length
        ? OPCIONES_NUEVAS()
        : actual.options,
    });
  }

  /** Lo mismo que comprueba el servidor, dicho antes de llegar a él. */
  function queFalta(): string | null {
    if (!titulo.trim()) return 'La encuesta necesita un título.';
    if (!bloqueada && !preguntas.length) return 'Agrega al menos una pregunta.';
    for (const [i, q] of preguntas.entries()) {
      if (!q.question_text.trim()) return `La pregunta ${i + 1} necesita un enunciado.`;
      if (TIPOS[q.question_type].conOpciones) {
        const conTexto = q.options.filter((o) => o.text.trim());
        if (conTexto.length < 2) {
          return `La pregunta ${i + 1} es de opciones: necesita al menos dos con texto.`;
        }
      }
    }
    return null;
  }

  function cuerpo(): CuerpoDeEncuesta {
    return {
      title: titulo.trim(),
      description: descripcion,
      is_anonymous: anonima,
      publish_to_feed: !editando && alFeed,
      send_email: !editando && porCorreo,
      reminder_hours: 0,
      questions: preguntas.map((q, i) => ({
        question_text: q.question_text.trim(),
        question_type: q.question_type,
        options: TIPOS[q.question_type].conOpciones
          ? q.options.filter((o) => o.text.trim())
          : null,
        is_required: q.is_required,
        order: i,
      })),
    };
  }

  function guardar() {
    const falta = queFalta();
    setError(falta);
    if (falta) return;
    if (!editando) { setConfirmando(true); return; }
    editar.mutate({ id: encuesta.id, cuerpo: cuerpo() }, {
      onSuccess: (r) => {
        Utils.toast(
          r.questions_locked
            ? 'Encuesta actualizada. Las preguntas no cambian: ya tiene respuestas.'
            : 'Encuesta actualizada',
          'success');
        onCerrar();
      },
      onError: (err) =>
        Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar', 'error'),
    });
  }

  function publicar() {
    crear.mutate(cuerpo(), {
      onSuccess: () => {
        Utils.toast('Encuesta publicada', 'success');
        setConfirmando(false);
        onCerrar();
      },
      onError: (err) => {
        setConfirmando(false);
        Utils.toast(err instanceof Error ? err.message : 'No se pudo crear', 'error');
      },
    });
  }

  return (
    <>
      <Drawer
        abierto
        onCambio={(v) => { if (!v && !guardando) onCerrar(); }}
        titulo={editando ? 'Editar encuesta' : 'Nueva encuesta'}
        descripcion={editando
          ? 'Una vez que alguien responde, solo cambian el título y la descripción.'
          : 'Las preguntas quedan fijas en cuanto alguien responda.'}
        ancho="lg"
        pie={
          <>
            <Button variant="ghost" onClick={onCerrar} disabled={guardando}>Cancelar</Button>
            <Button onClick={guardar} loading={guardando}>
              <i className={`bi ${editando ? 'bi-floppy' : 'bi-cloud-upload'}`} aria-hidden="true" />
              {editando ? 'Guardar cambios' : 'Crear encuesta'}
            </Button>
          </>
        }
      >
        {editando && cargada.isPending ? (
          <div className="flex flex-col gap-3" aria-busy="true">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : editando && cargada.isError ? (
          <EstadoError
            err={cargada.error}
            que="la encuesta"
            onReintentar={() => void cargada.refetch()}
          />
        ) : (
          <div className="flex flex-col gap-4">
            {bloqueada && (
              <p className="flex items-start gap-2 rounded-sm border border-warn/40 bg-warn/12 p-3 text-2 text-warn-ink">
                <i className="bi bi-lock-fill mt-0.5 shrink-0" aria-hidden="true" />
                Ya tiene respuestas: puedes corregir el título y la descripción, y nada más.
                Cambiar las preguntas o el tipo cambiaría el significado de lo ya respondido.
              </p>
            )}

            <Field label="Título">
              <Input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ej: Clima laboral Q2 2026"
                maxLength={200}
              />
            </Field>

            <Field label="Descripción" opcional>
              <Textarea
                rows={2}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Explica para qué es la encuesta…"
              />
            </Field>

            <TipoDeEncuesta anonima={anonima} onCambio={setAnonima} bloqueada={bloqueada} />

            {!editando && (
              <fieldset className="flex flex-wrap gap-4">
                <legend className="mb-1 text-3 font-semibold text-ink">Al publicar</legend>
                <Casilla puesta={alFeed} onCambio={setAlFeed} icono="bi-chat-square">
                  Publicar en el muro
                </Casilla>
                <Casilla puesta={porCorreo} onCambio={setPorCorreo} icono="bi-envelope">
                  Avisar por correo
                </Casilla>
              </fieldset>
            )}

            <hr className="border-line" />

            <Preguntas
              preguntas={preguntas}
              bloqueada={bloqueada}
              onCambiar={cambiarPregunta}
              onTipo={cambiarTipo}
              onQuitar={(i) => setPreguntas((ps) => ps.filter((_, j) => j !== i))}
              onAgregar={() => setPreguntas((ps) => [...ps, PREGUNTA_NUEVA()])}
              onMover={(i, salto) => setPreguntas((ps) => {
                const otro = i + salto;
                if (otro < 0 || otro >= ps.length) return ps;
                const copia = [...ps];
                [copia[i], copia[otro]] = [copia[otro]!, copia[i]!];
                return copia;
              })}
            />

            {error && (
              <p role="alert" className="text-3 font-semibold text-danger-ink">
                <i className="bi bi-exclamation-circle" aria-hidden="true" /> {error}
              </p>
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        abierto={confirmando}
        onCambio={(v) => { if (!guardando) setConfirmando(v); }}
        titulo="Publicar la encuesta"
        descripcion={
          (alFeed || porCorreo
            ? `Se publicará${alFeed ? ' en el muro' : ''}${alFeed && porCorreo ? ' y' : ''}${porCorreo ? ' por correo a todo el equipo' : ''}. `
            : 'Quedará disponible para todo el equipo. ')
          + 'En cuanto alguien responda, las preguntas quedan fijas.'
        }
        textoConfirmar="Publicar"
        cargando={crear.isPending}
        onConfirmar={publicar}
      />
    </>
  );
}

/* ── Anónima o identificada ────────────────────────────────────────────── */

const TIPOS_DE_ENCUESTA = [
  {
    anonima: true, icono: 'bi-incognito', titulo: 'Anónima',
    texto: 'No sabrás quién respondió qué. Para clima y opinión sincera.',
  },
  {
    anonima: false, icono: 'bi-person-vcard', titulo: 'Identificada',
    texto: 'Verás la respuesta de cada persona. Para evidencias e informes.',
  },
] as const;

function TipoDeEncuesta({
  anonima, onCambio, bloqueada,
}: {
  anonima: boolean;
  onCambio: (v: boolean) => void;
  bloqueada: boolean;
}) {
  if (bloqueada) {
    return (
      <p className="flex items-center gap-2 text-3">
        <span className="text-ink-2">Tipo:</span>
        <Badge tone={anonima ? 'info' : 'warn'}>
          <i className={anonima ? 'bi bi-incognito' : 'bi bi-person-vcard'} aria-hidden="true" />
          {anonima ? 'Anónima' : 'Identificada'}
        </Badge>
      </p>
    );
  }
  return (
    <fieldset>
      <legend className="mb-1.5 text-3 font-semibold text-ink">Tipo de encuesta</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {TIPOS_DE_ENCUESTA.map((t) => (
          <label key={t.titulo}>
            <input
              type="radio"
              name="tipo-de-encuesta"
              className="peer sr-only"
              checked={anonima === t.anonima}
              onChange={() => onCambio(t.anonima)}
            />
            <span
              className={cn(
                'flex h-full cursor-pointer items-start gap-2 rounded-sm border border-line bg-surface p-3',
                'hover:border-line-strong peer-focus-visible:focus-anillo',
                'peer-checked:border-brand-ink peer-checked:bg-brand/8',
              )}
            >
              <i className={cn('bi mt-0.5 text-4 text-brand-ink', t.icono)} aria-hidden="true" />
              <span className="min-w-0">
                <strong className="block text-3 text-ink">{t.titulo}</strong>
                <span className="block text-2 text-muted">{t.texto}</span>
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Casilla({
  puesta, onCambio, icono, children,
}: {
  puesta: boolean;
  onCambio: (v: boolean) => void;
  icono: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-3 text-ink">
      <input
        type="checkbox"
        checked={puesta}
        onChange={(e) => onCambio(e.target.checked)}
        className="size-4 accent-[var(--color-brand)] focus-visible:focus-anillo"
      />
      <i className={cn('bi text-ink-2', icono)} aria-hidden="true" />
      {children}
    </label>
  );
}

/* ── El editor de preguntas ────────────────────────────────────────────── */

function Preguntas({
  preguntas, bloqueada, onCambiar, onTipo, onQuitar, onAgregar, onMover,
}: {
  preguntas: Borrador[];
  bloqueada: boolean;
  onCambiar: (i: number, cambio: Partial<Borrador>) => void;
  onTipo: (i: number, tipo: TipoDePregunta) => void;
  onQuitar: (i: number) => void;
  onAgregar: () => void;
  onMover: (i: number, salto: number) => void;
}) {
  return (
    <section aria-labelledby="enc-preguntas">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 id="enc-preguntas" className="text-3 font-bold text-brand-ink">
          <i className="bi bi-list-ol" aria-hidden="true" /> Preguntas ({preguntas.length})
        </h3>
        {!bloqueada && (
          <Button variant="secondary" size="sm" onClick={onAgregar}>
            <i className="bi bi-plus-circle" aria-hidden="true" /> Agregar pregunta
          </Button>
        )}
      </div>

      {!preguntas.length ? (
        <p className="rounded-sm border border-line bg-sunken p-3 text-3 text-ink-2">
          <i className="bi bi-info-circle" aria-hidden="true" /> Agrega al menos una pregunta
          para que el equipo pueda responder.
        </p>
      ) : bloqueada ? (
        <ol className="flex flex-col gap-2">
          {preguntas.map((q, i) => (
            <li key={i} className="rounded-sm border border-line bg-sunken p-3">
              <p className="text-3 text-ink">{i + 1}. {q.question_text}</p>
              <p className="mt-0.5 text-2 text-muted">
                {TIPOS[q.question_type].etiqueta}
                {q.is_required ? ' · obligatoria' : ''}
                {q.options.length ? ` · ${q.options.length} opciones` : ''}
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <ol className="flex flex-col gap-3">
          {preguntas.map((q, i) => (
            <li key={i} className="rounded-sm border border-line bg-sunken p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <strong className="text-3 text-brand-ink">Pregunta {i + 1}</strong>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm" iconOnly
                    aria-label={`Subir la pregunta ${i + 1}`}
                    disabled={i === 0}
                    razonDeshabilitado="Ya es la primera"
                    onClick={() => onMover(i, -1)}
                  >
                    <i className="bi bi-arrow-up" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost" size="sm" iconOnly
                    aria-label={`Bajar la pregunta ${i + 1}`}
                    disabled={i === preguntas.length - 1}
                    razonDeshabilitado="Ya es la última"
                    onClick={() => onMover(i, 1)}
                  >
                    <i className="bi bi-arrow-down" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost" size="sm" iconOnly
                    aria-label={`Quitar la pregunta ${i + 1}`}
                    onClick={() => onQuitar(i)}
                  >
                    <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                  </Button>
                </div>
              </div>

              <Field label="Enunciado" className="mb-2">
                <Input
                  value={q.question_text}
                  onChange={(e) => onCambiar(i, { question_text: e.target.value })}
                  placeholder="Escribe la pregunta…"
                />
              </Field>

              <div className="flex flex-wrap items-end gap-3">
                <Field label="Tipo" className="min-w-[12rem] flex-1">
                  <Select
                    value={q.question_type}
                    onChange={(e) => onTipo(i, e.target.value as TipoDePregunta)}
                  >
                    {(Object.keys(TIPOS) as TipoDePregunta[]).map((t) => (
                      <option key={t} value={t}>{TIPOS[t].etiqueta}</option>
                    ))}
                  </Select>
                </Field>
                <Casilla
                  puesta={q.is_required}
                  onCambio={(v) => onCambiar(i, { is_required: v })}
                  icono="bi-asterisk"
                >
                  Obligatoria
                </Casilla>
              </div>

              {TIPOS[q.question_type].conOpciones && (
                <Opciones
                  opciones={q.options}
                  onCambio={(options) => onCambiar(i, { options })}
                  numero={i + 1}
                />
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Opciones({
  opciones, onCambio, numero,
}: {
  opciones: OpcionDePregunta[];
  onCambio: (v: OpcionDePregunta[]) => void;
  numero: number;
}) {
  return (
    <div className="mt-2">
      <p className="mb-1 text-2 text-muted">Opciones de respuesta (al menos dos):</p>
      <ul className="flex flex-col gap-1.5">
        {opciones.map((o, j) => (
          <li key={o.key} className="flex items-center gap-2">
            <span className="w-5 shrink-0 text-center text-2 font-bold text-muted">
              {o.key.toUpperCase()}
            </span>
            <InputSuelto
              id={`p${numero}-o${o.key}`}
              aria-label={`Opción ${o.key.toUpperCase()} de la pregunta ${numero}`}
              className="flex-1"
              value={o.text}
              onChange={(e) =>
                onCambio(opciones.map((x, k) => (k === j ? { ...x, text: e.target.value } : x)))}
              placeholder={`Opción ${o.key.toUpperCase()}`}
            />
            <Button
              variant="ghost" size="sm" iconOnly
              aria-label={`Quitar la opción ${o.key.toUpperCase()}`}
              disabled={opciones.length <= 2}
              razonDeshabilitado="Una pregunta de opciones necesita al menos dos"
              onClick={() => onCambio(opciones.filter((_, k) => k !== j))}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="ghost" size="sm" className="mt-1"
        onClick={() => onCambio([
          ...opciones,
          { key: String.fromCharCode(97 + opciones.length), text: '' },
        ])}
      >
        <i className="bi bi-plus" aria-hidden="true" /> Agregar opción
      </Button>
    </div>
  );
}
